import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "FV4005 Stage II",
    description:
      "The FV4005 Stage II (97038) recreates one of the most extraordinary experimental tank destroyers ever developed. Built from 2,080 pieces in 1:28 scale, this highly detailed model combines historical authenticity, realistic functions, and an engaging building experience.\n\nThe model faithfully captures the distinctive appearance of the British FV4005 Stage II, featuring its enormous turret, long 183 mm gun, realistic desert camouflage, and detailed suspension. Numerous authentic details make it an impressive display piece for military model enthusiasts.\n\nRemovable roof panels provide access to the fighting compartment, while the fully rotating turret and adjustable main gun add interactive functionality. The model also features detailed internal construction and realistic running gear, offering both an enjoyable build and an eye-catching collectible.\n\nPerfect for military history enthusiasts, advanced builders, and collectors looking for a unique armored vehicle model.",
    historicalTitle: "Historical Background",
    historical:
      "The FV4005 Stage II was an experimental British tank destroyer developed during the 1950s to counter the growing threat of heavily armored Soviet tanks. Built on the chassis of the Centurion tank, it was equipped with the enormous 183 mm L4 gun, one of the largest tank guns ever installed on an armored vehicle.\n\nThe massive turret was lightly armored and primarily designed to accommodate the huge gun and its ammunition. While the weapon offered tremendous firepower capable of destroying virtually any contemporary tank, the vehicle suffered from poor balance, excessive recoil, and limited practicality in combat.\n\nOnly a single prototype of the FV4005 Stage II was completed. Although it never entered mass production, it remains one of the most iconic experimental armored vehicles in British military history and continues to fascinate historians and military vehicle enthusiasts alike.",
    highlightsTitle: "Key Features",
    features: [
      "2,080 high-quality building pieces",
      "1:28 scale FV4005 Stage II replica",
      "Fully rotating turret",
      "Adjustable main gun",
      "Removable roof panels",
      "Detailed interior",
      "Realistic suspension and tracks",
      "Premium display model",
      "Compatible with leading building brick brands",
      "Designed for builders aged 14+",
    ],
    specsTitle: "Product Features",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Model:</strong> FV4005 Stage II</li>",
      "<li><strong>Model Number:</strong> 97038</li>",
      "<li><strong>Series:</strong> Military Series</li>",
      "<li><strong>Pieces:</strong> 2,080</li>",
      "<li><strong>Scale:</strong> 1:28</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
    ],
  },
  pl: {
    title: "FV4005 Stage II",
    description:
      "FV4005 Stage II (97038) to wyjątkowy model z klocków przedstawiający jeden z najbardziej niezwykłych eksperymentalnych niszczycieli czołgów w historii. Zestaw składa się z 2080 elementów i został wykonany w skali 1:28, oferując wysoki poziom odwzorowania, realistyczne funkcje oraz satysfakcjonujące doświadczenie podczas budowy.\n\nModel wiernie odwzorowuje charakterystyczny wygląd brytyjskiego FV4005 Stage II z ogromną wieżą, potężnym działem kalibru 183 mm, pustynnym kamuflażem oraz szczegółowo wykonanym układem jezdnym. Bogactwo detali sprawia, że model doskonale prezentuje się na półce kolekcjonera.\n\nZdejmowane panele dachu umożliwiają dostęp do wnętrza pojazdu. Wieża obraca się, a lufa działa posiada możliwość regulacji położenia. Szczegółowo odwzorowane wnętrze oraz realistyczne zawieszenie czynią model atrakcyjnym zarówno podczas budowy, jak i ekspozycji.\n\nTo doskonały wybór dla miłośników historii wojskowości, kolekcjonerów oraz zaawansowanych konstruktorów.",
    historicalTitle: "Tło historyczne",
    historical:
      "FV4005 Stage II był eksperymentalnym brytyjskim niszczycielem czołgów opracowanym w latach 50. XX wieku jako odpowiedź na pojawienie się ciężko opancerzonych radzieckich czołgów. Konstrukcję oparto na podwoziu czołgu Centurion i wyposażono w potężne działo L4 kalibru 183 mm, należące do największych dział zamontowanych kiedykolwiek w pojeździe pancernym.\n\nOgromna wieża została zaprojektowana głównie z myślą o pomieszczeniu działa i amunicji, dlatego jej opancerzenie było stosunkowo lekkie. Choć siła ognia była imponująca i pozwalała zniszczyć praktycznie każdy czołg tamtego okresu, pojazd cierpiał na problemy z wyważeniem, silnym odrzutem oraz ograniczoną praktycznością bojową.\n\nPowstał jedynie jeden prototyp FV4005 Stage II. Mimo że projekt nie wszedł do produkcji seryjnej, do dziś pozostaje jednym z najbardziej rozpoznawalnych eksperymentalnych brytyjskich pojazdów pancernych oraz ciekawostką w historii rozwoju broni pancernej.",
    highlightsTitle: "Cechy produktu",
    features: [
      "2080 elementów",
      "Szczegółowy model FV4005 Stage II w skali 1:28",
      "Obrotowa wieża",
      "Ruchoma armata",
      "Zdejmowane panele górne",
      "Szczegółowo odwzorowane wnętrze",
      "Realistyczne zawieszenie i gąsienice",
      "Wysokiej jakości model kolekcjonerski",
      "Kompatybilny z klockami wiodących producentów",
      "Wiek: 14+",
    ],
    specsTitle: "Cechy zestawu",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Model:</strong> FV4005 Stage II</li>",
      "<li><strong>Numer modelu:</strong> 97038</li>",
      "<li><strong>Seria:</strong> Military Series</li>",
      "<li><strong>Liczba elementów:</strong> 2080</li>",
      "<li><strong>Skala:</strong> 1:28</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
    ],
  },
  de: {
    title: "FV4005 Stage II",
    description:
      "Der FV4005 Stage II (97038) ist ein außergewöhnliches Klemmbausteinmodell eines der ungewöhnlichsten experimentellen Jagdpanzer der Militärgeschichte. Das Modell besteht aus 2.080 Teilen im Maßstab 1:28 und vereint historische Genauigkeit, realistische Funktionen und ein anspruchsvolles Bauerlebnis.\n\nDas Modell bildet den markanten FV4005 Stage II originalgetreu nach – mit seinem riesigen Turm, der langen 183-mm-Kanone, der authentischen Wüstentarnung sowie einem detaillierten Fahrwerk. Zahlreiche realistische Details machen das Modell zu einem beeindruckenden Ausstellungsstück.\n\nAbnehmbare Dachplatten ermöglichen einen Blick in den Kampfraum. Der Turm ist vollständig drehbar und die Hauptkanone höhenverstellbar. Das detaillierte Innenleben sowie die realistische Federung sorgen für zusätzlichen Spiel- und Ausstellungswert.\n\nDieses Set eignet sich perfekt für Militärgeschichtsinteressierte, Sammler und erfahrene Klemmbausteinbauer.",
    historicalTitle: "Historischer Hintergrund",
    historical:
      "Der FV4005 Stage II war ein experimenteller britischer Jagdpanzer, der in den 1950er-Jahren entwickelt wurde, um der Bedrohung durch schwer gepanzerte sowjetische Kampfpanzer entgegenzuwirken. Das Fahrzeug basierte auf dem Fahrgestell des Centurion-Panzers und war mit der gewaltigen 183-mm-L4-Kanone ausgestattet – einer der größten Panzerkanonen, die jemals in ein Kettenfahrzeug eingebaut wurden.\n\nDer riesige Turm war hauptsächlich dafür ausgelegt, die enorme Kanone und ihre Munition aufzunehmen, weshalb seine Panzerung vergleichsweise dünn ausfiel. Obwohl die Feuerkraft außergewöhnlich war und praktisch jeden damaligen Panzer hätte zerstören können, führten das enorme Gewicht, der starke Rückstoß und die eingeschränkte Beweglichkeit dazu, dass das Projekt nie in Serie ging.\n\nVom FV4005 Stage II wurde lediglich ein einziger Prototyp gebaut. Trotz seiner kurzen Entwicklungsgeschichte gilt er bis heute als eines der faszinierendsten experimentellen Panzerfahrzeuge Großbritanniens.",
    highlightsTitle: "Wichtige Merkmale",
    features: [
      "2.080 Teile",
      "Detailreiche Nachbildung des FV4005 Stage II im Maßstab 1:28",
      "Vollständig drehbarer Turm",
      "Höhenverstellbare Hauptkanone",
      "Abnehmbare Dachplatten",
      "Detaillierter Innenraum",
      "Realistische Federung und Laufwerk",
      "Hochwertiges Sammlermodell",
      "Kompatibel mit Klemmbausteinen führender Hersteller",
      "Altersempfehlung: 14+",
    ],
    specsTitle: "Produkteigenschaften",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Modell:</strong> FV4005 Stage II</li>",
      "<li><strong>Modellnummer:</strong> 97038</li>",
      "<li><strong>Serie:</strong> Military Series</li>",
      "<li><strong>Teile:</strong> 2.080</li>",
      "<li><strong>Maßstab:</strong> 1:28</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
    ],
  },
  es: {
    title: "FV4005 Stage II",
    description:
      "El FV4005 Stage II (97038) es un impresionante modelo de bloques de construcción que reproduce uno de los cazacarros experimentales más singulares jamás desarrollados. Con 2.080 piezas y una escala 1:28, combina un elevado nivel de detalle, funciones realistas y una experiencia de construcción de primer nivel.\n\nEl modelo recrea fielmente el característico FV4005 Stage II, incluyendo su enorme torre, el cañón de 183 mm, el camuflaje desértico y un detallado sistema de suspensión. Su excelente acabado lo convierte en una magnífica pieza de exposición.\n\nLos paneles superiores desmontables permiten observar el compartimento interior. La torre gira completamente y el cañón principal puede ajustarse en elevación. La detallada estructura interna y el realista tren de rodaje aportan aún más autenticidad al conjunto.\n\nEs una opción ideal para coleccionistas, aficionados a la historia militar y constructores experimentados.",
    historicalTitle: "Contexto histórico",
    historical:
      "El FV4005 Stage II fue un cazacarros experimental británico desarrollado durante la década de 1950 para enfrentarse a los nuevos carros pesados soviéticos. Basado en el chasis del Centurion, estaba equipado con el enorme cañón L4 de 183 mm, una de las armas de mayor calibre instaladas en un vehículo blindado.\n\nLa enorme torre fue diseñada principalmente para alojar el gigantesco cañón y su munición, por lo que disponía de un blindaje relativamente ligero. Aunque su potencia de fuego era extraordinaria, los problemas derivados del enorme retroceso, el equilibrio del vehículo y su limitada movilidad impidieron que el proyecto pasara a producción.\n\nSolo se construyó un prototipo del FV4005 Stage II. A pesar de ello, sigue siendo uno de los vehículos blindados experimentales más conocidos de la historia militar británica y una pieza muy apreciada por los aficionados a los vehículos históricos.",
    highlightsTitle: "Características clave",
    features: [
      "2.080 piezas",
      "Réplica detallada del FV4005 Stage II a escala 1:28",
      "Torre totalmente giratoria",
      "Cañón principal ajustable",
      "Paneles superiores desmontables",
      "Interior detallado",
      "Suspensión y orugas realistas",
      "Modelo de exposición de alta calidad",
      "Compatible con las principales marcas de bloques de construcción",
      "Edad recomendada: 14+",
    ],
    specsTitle: "Características del producto",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Modelo:</strong> FV4005 Stage II</li>",
      "<li><strong>Número de modelo:</strong> 97038</li>",
      "<li><strong>Serie:</strong> Military Series</li>",
      "<li><strong>Piezas:</strong> 2.080</li>",
      "<li><strong>Escala:</strong> 1:28</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
    ],
  },
  fr: {
    title: "FV4005 Stage II",
    description:
      "Le FV4005 Stage II (97038) est un remarquable modèle en briques de construction reproduisant l'un des chasseurs de chars expérimentaux les plus impressionnants de l'histoire militaire. Composé de 2 080 pièces à l'échelle 1:28, il offre un haut niveau de réalisme, des fonctions interactives et une expérience de montage passionnante.\n\nLe modèle reproduit fidèlement le FV4005 Stage II avec son immense tourelle, son puissant canon de 183 mm, son camouflage couleur sable et son train de roulement richement détaillé. Les nombreux détails en font une superbe pièce d'exposition.\n\nLes panneaux supérieurs amovibles permettent d'accéder à l'intérieur du véhicule. La tourelle pivote entièrement et le canon principal peut être relevé ou abaissé. La structure interne détaillée et la suspension réaliste renforcent encore l'authenticité du modèle.\n\nCe modèle constitue un excellent choix pour les passionnés d'histoire militaire, les collectionneurs et les amateurs de constructions techniques.",
    historicalTitle: "Contexte historique",
    historical:
      "Le FV4005 Stage II était un chasseur de chars expérimental britannique développé dans les années 1950 afin de faire face aux chars lourds soviétiques. Construit sur le châssis du Centurion, il était équipé du gigantesque canon L4 de 183 mm, l'un des plus gros canons jamais montés sur un véhicule blindé.\n\nSa tourelle de très grande taille était principalement conçue pour accueillir cette arme imposante et ses munitions, ce qui limitait fortement son blindage. Bien que sa puissance de feu ait été exceptionnelle, les problèmes liés au recul, à l'équilibre du véhicule et à sa mobilité empêchèrent toute production en série.\n\nUn seul prototype du FV4005 Stage II fut construit. Malgré cela, il demeure aujourd'hui l'un des véhicules blindés expérimentaux les plus célèbres de l'histoire militaire britannique.",
    highlightsTitle: "Points clés",
    features: [
      "2 080 pièces",
      "Réplique fidèle du FV4005 Stage II à l'échelle 1:28",
      "Tourelle entièrement rotative",
      "Canon principal orientable",
      "Panneaux supérieurs amovibles",
      "Intérieur détaillé",
      "Suspension et train de roulement réalistes",
      "Modèle d'exposition haut de gamme",
      "Compatible avec les principales marques de briques de construction",
      "Âge recommandé : 14 ans et plus",
    ],
    specsTitle: "Caractéristiques produit",
    specs: [
      "<li><strong>Marque:</strong> 12GO</li>",
      "<li><strong>Modèle:</strong> FV4005 Stage II</li>",
      "<li><strong>Référence:</strong> 97038</li>",
      "<li><strong>Série:</strong> Military Series</li>",
      "<li><strong>Nombre de pièces:</strong> 2 080</li>",
      "<li><strong>Échelle:</strong> 1:28</li>",
      "<li><strong>Âge recommandé:</strong> 14 ans et plus</li>",
    ],
  },
  nl: {
    title: "FV4005 Stage II",
    description:
      "De FV4005 Stage II (97038) is een indrukwekkend bouwstenenmodel van een van de meest bijzondere experimentele tankjagers ooit ontwikkeld. Het model bestaat uit 2.080 onderdelen op schaal 1:28 en combineert historische nauwkeurigheid, realistische functies en een uitdagende bouwervaring.\n\nHet model geeft de karakteristieke vorm van de Britse FV4005 Stage II nauwkeurig weer, inclusief de enorme geschutskoepel, het krachtige 183 mm-kanon, authentieke woestijncamouflage en een gedetailleerd onderstel. Dankzij de vele zorgvuldig uitgewerkte details is het model een indrukwekkend verzamelobject.\n\nAfneembare bovenpanelen geven toegang tot het interieur, terwijl de geschutskoepel volledig draaibaar is en het hoofdkanon in hoogte verstelbaar is. De gedetailleerde binnenzijde en realistische ophanging maken dit model zowel tijdens het bouwen als na voltooiing bijzonder aantrekkelijk.\n\nEen uitstekende keuze voor liefhebbers van militaire geschiedenis, verzamelaars en ervaren bouwers.",
    historicalTitle: "Historische achtergrond",
    historical:
      "De FV4005 Stage II was een experimentele Britse tankjager die in de jaren 50 werd ontwikkeld als antwoord op de steeds zwaarder gepantserde Sovjettanks. Het voertuig was gebaseerd op het onderstel van de Centurion en uitgerust met het enorme 183 mm L4-kanon, een van de grootste tankkanonnen die ooit op een gepantserd voertuig zijn gemonteerd.\n\nDe enorme geschutskoepel was voornamelijk ontworpen om het gigantische kanon en de bijbehorende munitie te huisvesten, waardoor de bepantsering relatief dun bleef. Hoewel de vuurkracht uitzonderlijk was, maakten het enorme gewicht, de krachtige terugslag en de beperkte mobiliteit het voertuig ongeschikt voor serieproductie.\n\nVan de FV4005 Stage II werd uiteindelijk slechts één prototype gebouwd. Ondanks het uitblijven van massaproductie geldt het voertuig nog altijd als een van de meest opvallende experimentele pantservoertuigen uit de Britse militaire geschiedenis.",
    highlightsTitle: "Belangrijkste kenmerken",
    features: [
      "2.080 onderdelen",
      "Gedetailleerde replica van de FV4005 Stage II op schaal 1:28",
      "Volledig draaibare geschutskoepel",
      "Verstelbaar hoofdkanon",
      "Afneembare bovenpanelen",
      "Gedetailleerd interieur",
      "Realistische ophanging en rupsen",
      "Hoogwaardig displaymodel",
      "Compatibel met bouwstenen van de belangrijkste merken",
      "Aanbevolen leeftijd: 14+",
    ],
    specsTitle: "Productkenmerken",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Model:</strong> FV4005 Stage II</li>",
      "<li><strong>Modelnummer:</strong> 97038</li>",
      "<li><strong>Serie:</strong> Military Series</li>",
      "<li><strong>Onderdelen:</strong> 2.080</li>",
      "<li><strong>Schaal:</strong> 1:28</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
    ],
  },
  zh: {
    title: "FV4005 Stage II",
    description:
      "FV4005 Stage II（97038）是一款高度还原英国实验型坦克歼击车的积木模型。全套包含 2,080 块积木，采用 1:28 比例设计，将历史还原、丰富细节与出色的拼搭体验完美结合。\n\n模型忠实再现了 FV4005 Stage II 极具辨识度的外观，包括巨大的炮塔、威力惊人的 183 毫米主炮、沙漠迷彩涂装以及精细的悬挂和履带系统。丰富的细节使其成为极具收藏价值的展示模型。\n\n顶部装甲可拆卸，可展示内部战斗舱结构；炮塔支持 360°旋转，主炮可上下俯仰调节。精细的内部构造和真实的机械细节不仅提升了拼搭乐趣，也让成品更加逼真。\n\n无论是军事模型收藏者、历史爱好者，还是高级积木玩家，这款模型都是值得珍藏的精品之作。",
    historicalTitle: "历史背景",
    historical:
      "FV4005 Stage II 是英国于 20 世纪 50 年代研发的一款实验型坦克歼击车，旨在应对当时不断发展的苏联重型坦克。该车采用 Centurion（百夫长）坦克底盘，并安装了威力巨大的 183 毫米 L4 火炮，这是历史上安装在装甲车辆上的最大口径坦克炮之一。\n\n为了容纳这门巨炮及其弹药，FV4005 配备了一座体积巨大的炮塔，但其装甲相对较薄。虽然火力极其强大，理论上能够摧毁当时几乎所有主战坦克，但巨大的后坐力、整车重量以及有限的机动性使该项目最终未能投入量产。\n\nFV4005 Stage II 最终仅制造了一辆原型车。尽管从未正式服役，它仍被认为是英国装甲车辆发展史上最具代表性的实验型坦克之一，并深受军事历史爱好者和模型收藏者的关注。",
    highlightsTitle: "主要特点",
    features: [
      "2,080 块积木",
      "高度还原 FV4005 Stage II（1:28）",
      "炮塔可 360° 旋转",
      "主炮支持俯仰调节",
      "可拆卸顶部装甲",
      "精细内部结构",
      "真实悬挂与履带系统",
      "高品质收藏展示模型",
      "兼容主流积木品牌",
      "建议年龄：14 岁以上",
    ],
    specsTitle: "产品规格",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>型号：</strong>FV4005 Stage II</li>",
      "<li><strong>产品编号：</strong>97038</li>",
      "<li><strong>系列：</strong>Military Series</li>",
      "<li><strong>积木数量：</strong>2,080 块</li>",
      "<li><strong>比例：</strong>1:28</li>",
      "<li><strong>建议年龄：</strong>14 岁以上</li>",
    ],
  },

  it: {
    title: "FV4005 Stage II",
    description:
      "Il FV4005 Stage II (97038) ricrea uno dei più straordinari cacciacarri sperimentali mai sviluppati. Realizzato con 2.080 pezzi in scala 1:28, questo modello altamente dettagliato combina autenticità storica, funzioni realistiche e un'esperienza di costruzione coinvolgente.\n\nIl modello riproduce fedelmente l'aspetto distintivo del britannico FV4005 Stage II, con la sua enorme torretta, il lungo cannone da 183 mm, il realistico mimetismo desertico e le sospensioni dettagliate. Numerosi particolari autentici lo rendono un eccezionale pezzo da esposizione per gli appassionati di modellismo militare.\n\nI pannelli rimovibili del tetto consentono di accedere al compartimento di combattimento, mentre la torretta completamente girevole e il cannone principale regolabile aggiungono funzionalità interattive. Il modello presenta inoltre una costruzione interna dettagliata e un realistico apparato di rotolamento, offrendo sia un'esperienza di montaggio piacevole sia un accattivante oggetto da collezione.\n\nPerfetto per gli appassionati di storia militare, i costruttori esperti e i collezionisti alla ricerca di un modello unico di veicolo corazzato.",
    historicalTitle: "Contesto storico",
    historical:
      "Il FV4005 Stage II fu un cacciacarri sperimentale britannico sviluppato negli anni Cinquanta per contrastare la crescente minaccia dei carri armati sovietici pesantemente corazzati. Costruito sullo scafo del carro Centurion, era equipaggiato con l'enorme cannone L4 da 183 mm, una delle più grandi armi da carro mai installate su un veicolo corazzato.\n\nLa massiccia torretta era leggermente corazzata e progettata principalmente per alloggiare l'enorme cannone e le relative munizioni. Sebbene l'arma offrisse una potenza di fuoco straordinaria, capace di distruggere praticamente qualsiasi carro contemporaneo, il veicolo soffriva di scarso equilibrio, rinculo eccessivo e limitata praticità in combattimento.\n\nFu completato un solo prototipo del FV4005 Stage II. Sebbene non sia mai entrato in produzione di serie, rimane uno dei più iconici veicoli corazzati sperimentali della storia militare britannica e continua ad affascinare storici e appassionati di veicoli militari.",
    highlightsTitle: "Caratteristiche principali",
    features: [
      "2.080 pezzi da costruzione di alta qualità",
      "Replica del FV4005 Stage II in scala 1:28",
      "Torretta completamente girevole",
      "Cannone principale regolabile",
      "Pannelli del tetto rimovibili",
      "Interni dettagliati",
      "Sospensioni e cingoli realistici",
      "Modello da esposizione premium",
      "Compatibile con i principali marchi di mattoncini da costruzione",
      "Progettato per costruttori dai 14 anni in su",
    ],
    specsTitle: "Caratteristiche del prodotto",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Modello:</strong> FV4005 Stage II</li>",
      "<li><strong>Numero modello:</strong> 97038</li>",
      "<li><strong>Serie:</strong> Serie militare</li>",
      "<li><strong>Pezzi:</strong> 2.080</li>",
      "<li><strong>Scala:</strong> 1:28</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
    ],
  },
  ru: {
    title: "FV4005 Stage II",
    description:
      "FV4005 Stage II (97038) воссоздаёт одну из самых необычных экспериментальных противотанковых машин из когда-либо разработанных. Высокодетализированная модель из 2 080 деталей в масштабе 1:28 сочетает историческую достоверность, реалистичные функции и увлекательный процесс сборки.\n\nМодель точно передаёт характерный облик британской FV4005 Stage II: огромную башню, длинное 183-мм орудие, реалистичный пустынный камуфляж и детализированную подвеску. Многочисленные аутентичные детали делают её впечатляющим экспонатом для поклонников военной техники и моделизма.\n\nСъёмные панели крыши обеспечивают доступ к боевому отделению, а полностью вращающаяся башня и регулируемое основное орудие добавляют интерактивности. Модель также отличается детализированной внутренней конструкцией и реалистичной ходовой частью, сочетая приятную сборку с эффектным коллекционным видом.\n\nПрекрасный выбор для любителей военной истории, опытных сборщиков и коллекционеров, которые ищут уникальную модель бронированной машины.",
    historicalTitle: "Историческая справка",
    historical:
      "FV4005 Stage II была экспериментальной британской противотанковой машиной, разработанной в 1950-х годах для противодействия растущей угрозе со стороны тяжело бронированных советских танков. Построенная на шасси танка Centurion, она получила огромное 183-мм орудие L4 — одно из крупнейших танковых орудий, когда-либо установленных на бронированную машину.\n\nМассивная башня имела лёгкое бронирование и предназначалась главным образом для размещения огромного орудия и его боекомплекта. Несмотря на колоссальную огневую мощь, способную уничтожить практически любой современный ей танк, машина страдала от плохой сбалансированности, чрезмерной отдачи и ограниченной практичности в бою.\n\nБыл построен только один прототип FV4005 Stage II. Хотя машина так и не пошла в серийное производство, она остаётся одной из самых знаковых экспериментальных бронированных машин в британской военной истории и продолжает вызывать интерес у историков и поклонников военной техники.",
    highlightsTitle: "Ключевые особенности",
    features: [
      "2 080 высококачественных деталей",
      "Реплика FV4005 Stage II в масштабе 1:28",
      "Полностью вращающаяся башня",
      "Регулируемое основное орудие",
      "Съёмные панели крыши",
      "Детализированный интерьер",
      "Реалистичная подвеска и гусеницы",
      "Премиальная модель для демонстрации",
      "Совместимость с ведущими брендами строительных кубиков",
      "Предназначена для сборщиков от 14 лет",
    ],
    specsTitle: "Характеристики товара",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Модель:</strong> FV4005 Stage II</li>",
      "<li><strong>Номер модели:</strong> 97038</li>",
      "<li><strong>Серия:</strong> Военная серия</li>",
      "<li><strong>Детали:</strong> 2 080</li>",
      "<li><strong>Масштаб:</strong> 1:28</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
    ],
  },

} as const;

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.historicalTitle}</h2>

${paragraphs(c.historical)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_97038: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
